import './augmentation';
import { createTheme, type Theme } from '@mui/material/styles';
import type { Direction } from '@mui/material/styles';
import { withAlpha } from './color';
import {
  DEFAULT_PRIMARY,
  fontFamily,
  primaryTokens,
  radii,
  shadows,
  tokens,
  type PrimaryTokens,
  type SchemeTokens,
} from './tokens';

function scheme(t: SchemeTokens, p: PrimaryTokens) {
  return {
    palette: {
      primary: { main: p.p, contrastText: p.onP, container: p.pCont, onContainer: p.onPCont },
      success: { main: t.ok, contrastText: t.okC, container: t.okC, onContainer: t.ok },
      warning: { main: t.warn, contrastText: t.warnC, container: t.warnC, onContainer: t.warn },
      error: { main: t.err, contrastText: t.errC, container: t.errC, onContainer: t.err },
      info: { main: p.p, contrastText: p.onP, container: p.pCont, onContainer: p.onPCont },
      neutral: { main: t.slate, contrastText: t.slateC, container: t.slateC, onContainer: t.slate },
      background: { default: t.bg, paper: t.surf },
      surface: { main: t.surf, container: t.surf2, containerHigh: t.surf3 },
      text: { primary: t.fg, secondary: t.fg2, tertiary: t.fg3, disabled: withAlpha(t.fg, 0.45) },
      divider: t.line,
      outline: t.line2,
      scrim: t.scrim,
      action: {
        hover: withAlpha(t.fg, 0.06),
        selected: withAlpha(p.p, 0.08),
        disabled: withAlpha(t.fg, 0.45),
        disabledBackground: withAlpha(t.fg, 0.12),
        focus: withAlpha(p.p, 0.12),
      },
    },
  };
}

/**
 * MUI theme built from the design tokens. `primary` is the tenant brand colour; every
 * primary-derived token is recomputed from it (see tokens.primaryTokens).
 */
export function createAppTheme({
  primary = DEFAULT_PRIMARY,
  direction = 'ltr',
  locales = [],
}: { primary?: string | undefined; direction?: Direction; locales?: object[] } = {}): Theme {
  const p = primaryTokens(primary);
  const font = direction === 'rtl' ? fontFamily.arabic : fontFamily.latin;

  return createTheme(
    {
      direction,
      cssVariables: { colorSchemeSelector: 'data' },
      colorSchemes: { light: scheme(tokens.light, p.light), dark: scheme(tokens.dark, p.dark) },
      shape: { borderRadius: radii.field },
      spacing: 8,
      typography: {
        fontFamily: font,
        fontSize: 14,
        htmlFontSize: 16,
        h1: { fontSize: 40, lineHeight: '48px', fontWeight: 600, letterSpacing: '-0.01em' },
        h2: { fontSize: 32, lineHeight: '40px', fontWeight: 600 },
        h3: { fontSize: 28, lineHeight: '36px', fontWeight: 600 },
        h4: { fontSize: 26, lineHeight: '32px', fontWeight: 600 },
        h5: { fontSize: 22, lineHeight: '28px', fontWeight: 600 },
        h6: { fontSize: 20, lineHeight: '28px', fontWeight: 600 },
        title: { fontSize: 18, lineHeight: '24px', fontWeight: 600, fontFamily: font },
        subtitle1: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
        subtitle2: { fontSize: 14, lineHeight: '20px', fontWeight: 600 },
        body1: { fontSize: 14, lineHeight: '20px' },
        body2: { fontSize: 13, lineHeight: '18px' },
        caption: { fontSize: 12, lineHeight: '16px' },
        overline: {
          fontSize: 11,
          lineHeight: '16px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        },
        button: { fontSize: 14, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
        kpi: {
          fontSize: 28,
          lineHeight: '32px',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          fontFamily: font,
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: (theme) => ({
            ':root': { '--p-base': primary },
            body: { fontSize: 14, lineHeight: '20px', WebkitFontSmoothing: 'antialiased' },
            '*:focus-visible': { outline: `3px solid ${theme.vars.palette.primary.main}`, outlineOffset: 2 },
            'input, select, textarea, button': { fontFamily: 'inherit' },
            a: { color: theme.vars.palette.primary.main, textDecoration: 'none' },
            'a:hover': { color: theme.vars.palette.primary.onContainer, textDecoration: 'underline' },
            '.tabular': { fontVariantNumeric: 'tabular-nums' },
            '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
            '@keyframes pulse': { '0%, 100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
            '@keyframes slideIn': {
              from: { transform: 'translateX(24px)', opacity: 0 },
              to: { transform: 'none', opacity: 1 },
            },
            '@keyframes fadeUp': {
              from: { transform: 'translateY(8px)', opacity: 0 },
              to: { transform: 'none', opacity: 1 },
            },
          }),
        },
        MuiButtonBase: { defaultProps: { disableRipple: false } },
        MuiButton: {
          defaultProps: { disableElevation: true, variant: 'contained' },
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: radii.pill,
              height: 40,
              padding: '0 20px',
              gap: 8,
              whiteSpace: 'nowrap',
              '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 },
              '&.Mui-disabled': { color: theme.vars.palette.action.disabled },
            }),
            sizeSmall: { height: 36, padding: '0 14px', fontSize: 13 },
            sizeLarge: { height: 48, padding: '0 24px', fontSize: 15 },
            contained: {
              '&:hover': { boxShadow: 'inset 0 0 0 40px rgba(255,255,255,.1)' },
            },
            outlined: ({ theme }) => ({
              borderColor: theme.vars.palette.outline,
              '&:hover': {
                borderColor: theme.vars.palette.outline,
                backgroundColor: theme.vars.palette.action.selected,
              },
            }),
            text: { padding: '0 12px' },
          },
          variants: [
            {
              props: { variant: 'tonal' },
              style: ({ theme }) => ({
                backgroundColor: theme.vars.palette.primary.container,
                color: theme.vars.palette.primary.onContainer,
                '&:hover': {
                  backgroundColor: theme.vars.palette.primary.container,
                  boxShadow: `inset 0 0 0 40px ${withAlpha('#000000', 0.04)}`,
                },
              }),
            },
            {
              props: { variant: 'contained', color: 'error' },
              style: { color: '#FFFFFF' },
            },
          ],
        },
        MuiIconButton: {
          styleOverrides: {
            root: ({ theme }) => ({ width: 40, height: 40, color: theme.vars.palette.text.secondary }),
            sizeSmall: { width: 36, height: 36 },
            sizeLarge: { width: 48, height: 48 },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: radii.field,
              backgroundColor: theme.vars.palette.background.paper,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.vars.palette.outline },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.vars.palette.text.secondary },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: theme.vars.palette.primary.main,
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: theme.vars.palette.error.main,
              },
              '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: withAlpha('#1B1C22', 0.14) },
            }),
            input: { padding: '10px 12px', height: 20, fontSize: 14 },
            inputSizeSmall: { padding: '8px 12px' },
            adornedStart: { paddingInlineStart: 10 },
          },
        },
        // Placeholders in the tertiary grey: with `inherit` they looked like typed values (design review).
        MuiInputBase: {
          styleOverrides: {
            input: ({ theme }) => ({
              '&::placeholder': { opacity: 1, color: theme.vars.palette.text.tertiary },
            }),
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: ({ theme }) => ({
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 600,
              color: theme.vars.palette.text.secondary,
              '&.Mui-focused': { color: theme.vars.palette.text.secondary },
              '&.Mui-error': { color: theme.vars.palette.error.main },
            }),
          },
        },
        MuiFormHelperText: {
          styleOverrides: { root: { marginInline: 0, marginTop: 4, fontSize: 12, lineHeight: '16px' } },
        },
        MuiCheckbox: { defaultProps: { size: 'small' } },
        MuiChip: {
          styleOverrides: {
            root: { borderRadius: radii.chip, fontWeight: 500, fontSize: 13, height: 32 },
            sizeSmall: { height: 24, fontSize: 12, fontWeight: 600 },
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: ({ theme }) => ({ minHeight: 44, borderBottom: `1px solid ${theme.vars.palette.divider}` }),
            indicator: { height: 3 },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme }) => ({
              minHeight: 44,
              padding: '0 16px',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 500,
              color: theme.vars.palette.text.secondary,
              gap: 8,
              '&.Mui-selected': { fontWeight: 600 },
              '& .MuiTab-iconWrapper': { margin: 0 },
            }),
          },
        },
        MuiPaper: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: { backgroundImage: 'none' },
            rounded: { borderRadius: radii.card },
            outlined: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
          },
        },
        MuiCard: { defaultProps: { variant: 'outlined' } },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: radii.dialog,
              boxShadow: shadows.dialog,
              backgroundImage: 'none',
              animation: 'fadeUp .15s ease-out',
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: { fontSize: 20, lineHeight: '28px', fontWeight: 600, padding: '24px 24px 8px' },
          },
        },
        MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
        MuiDialogActions: {
          styleOverrides: {
            root: { padding: '16px 24px 24px', gap: 8, '& > :not(style) ~ :not(style)': { marginLeft: 0 } },
          },
        },
        MuiBackdrop: {
          styleOverrides: {
            root: ({ theme, ownerState }) =>
              ownerState.invisible ? {} : { backgroundColor: theme.vars.palette.scrim },
          },
        },
        MuiPopover: { styleOverrides: { paper: { boxShadow: shadows.menu } } },
        MuiMenu: { styleOverrides: { paper: { borderRadius: radii.field } } },
        MuiMenuItem: { styleOverrides: { root: { minHeight: 40, fontSize: 14 } } },
        MuiSnackbarContent: {
          styleOverrides: {
            root: {
              backgroundColor: tokens.inverse.bg,
              color: tokens.inverse.fg,
              borderRadius: 8,
              minHeight: 48,
              boxShadow: shadows.snackbar,
              padding: '0 8px 0 16px',
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: tokens.inverse.bg,
              color: tokens.inverse.fg,
              fontSize: 12,
              lineHeight: '16px',
              padding: '6px 8px',
              borderRadius: 4,
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: ({ theme }) => ({
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.vars.palette.surface.containerHigh,
            }),
            bar: { borderRadius: 2 },
          },
        },
        MuiSkeleton: {
          defaultProps: { animation: 'pulse' },
          styleOverrides: {
            root: ({ theme }) => ({ backgroundColor: theme.vars.palette.surface.containerHigh }),
          },
        },
        MuiBreadcrumbs: {
          styleOverrides: {
            root: ({ theme }) => ({ fontSize: 13, color: theme.vars.palette.text.tertiary }),
            separator: { marginInline: 4 },
          },
        },
        MuiDataGrid: {
          // No per-column menu (pages offer « Colonnes »): its hover icon cut short headers to « Niv… ».
          defaultProps: {
            rowHeight: 40,
            columnHeaderHeight: 44,
            disableRowSelectionOnClick: true,
            disableColumnMenu: true,
          },
          styleOverrides: {
            root: ({ theme }) => ({
              borderColor: theme.vars.palette.divider,
              borderRadius: radii.card,
              backgroundColor: theme.vars.palette.background.paper,
              '--DataGrid-containerBackground': theme.vars.palette.surface.container,
              '--DataGrid-rowBorderColor': theme.vars.palette.divider,
              fontSize: 14,
            }),
            columnHeaderTitle: { fontSize: 13, fontWeight: 600 },
            row: ({ theme }) => ({
              '&:hover': { backgroundColor: theme.vars.palette.surface.container },
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: `color-mix(in srgb, ${theme.vars.palette.primary.main} 8%, ${theme.vars.palette.background.paper})`,
              },
            }),
            cell: { '&:focus, &:focus-within': { outline: 'none' } },
            footerContainer: { minHeight: 52 },
          },
        },
      },
    },
    ...locales,
  );
}
